import { apiConfig } from '@/config/api.config'
import { AUTH } from '@/constants/auth'
import { SocketEvent } from '@/constants/socket-events'
import { selectActiveTeam, useAuthStore } from '@/stores/auth.store'
import type { SocketContextValue } from '@/types/socket'
import Cookies from 'js-cookie'
import { createContext, useCallback, useEffect, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { toast } from 'sonner'

// eslint-disable-next-line react-refresh/only-export-components
export const SocketContext = createContext<SocketContextValue | null>(null)

export function SocketProvider({ children }: { children: React.ReactNode }) {
  const socket = useRef<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)

  const team = useAuthStore(selectActiveTeam)

  useEffect(() => {
    if (!socket.current) {
      socket.current = io(apiConfig.VITE_PUBLIC_SOCKET_URL, {
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        extraHeaders: {
          Authorization: `Bearer ${Cookies.get(AUTH.ACCESS_TOKEN)}`,
        },
      })

      socket.current.on('connect', () => {
        setIsConnected(true)
      })

      socket.current.on('disconnect', () => {
        setIsConnected(false)
      })

      socket.current.on('reconnect', (attemptNumber: number) => {
        toast.success(`Reconectado após ${attemptNumber} tentativa(s)`)
      })
    }

    return () => {
      if (socket.current) {
        socket.current.close()
        socket.current = null
      }
    }
  }, [])

  useEffect(() => {
    const teamId = team?.id
    if (teamId) {
      socket.current?.emit(SocketEvent.JOIN_TEAM, {
        teamId,
      })

      return () => {
        socket.current?.emit(SocketEvent.LEAVE_TEAM, { teamId })
      }
    }
  }, [team?.id])

  const emit = useCallback((event: string, data?: unknown) => {
    if (socket.current) {
      socket.current.emit(event, data)
    }
  }, [])

  const on = useCallback(
    (event: string, callback: (...args: unknown[]) => void) => {
      if (socket.current) {
        socket.current.on(event, callback)
      }
    },
    []
  )

  const off = useCallback(
    (event: string, callback?: (...args: unknown[]) => void) => {
      if (socket.current) {
        socket.current.off(event, callback)
      }
    },
    []
  )

  const value: SocketContextValue = {
    socket: socket.current,
    isConnected,
    emit,
    on,
    off,
  }

  return (
    <SocketContext.Provider value={value}>{children}</SocketContext.Provider>
  )
}
